import React, { useState, useEffect } from 'react'
import { MonacoDiffEditor } from "react-monaco-editor";
import formatXml from 'xml-formatter';
import { returnType } from "../../Utilities/ReturnType";
import { connect } from "react-redux";
import { GlobalAutoHealing } from "../../Interface/IGlobalAutoHealing";

const mapStateToProps = (state: GlobalAutoHealing) => ({
    reviewContent: state.reviewContent,
    replaceContent: state.replaceContent
});

const storeProps = returnType(mapStateToProps);
type StoreProps = typeof storeProps.returnType;

// Editor options
const options = {
    readOnly: true,
    selectOnLineNumbers: false,
    wordWrap: 'on' as any,
    minimap: { enabled: false },
    automaticLayout: true,
    scrollbar: { horizontal: 'auto' as string } as object
}

// Interface for the state of the component
interface IXmlDiffEditorState {
    original: string;
    value: string;
}

// Interface for the props of the component
interface IXmlDiffEditorProps {
    onDiffEditorChange: (type: string,value: string) => void;
}

/**
 * This component is used to display the diff editor for the xml content
 * https://www.npmjs.com/package/react-monaco-editor
 * https://github.com/microsoft/monaco-editor
 * @returns 
 */
function XmlDiffEditor(props: StoreProps & IXmlDiffEditorProps & { type: string, onDiffEditorChange: (type: string, value: string) => void, selectedResult: any }) {
    const [editorValue, setEditorValue] = useState<IXmlDiffEditorState>({
        original: '',
        value: ''
    })

    const handleOriginalText = () => {
        if (props.type === 'Navigation') {
            return props.reviewContent.NavOriginalText ? JSON.parse(JSON.stringify(props.reviewContent.NavOriginalText)) : '';
        }
        if (props.type === 'Content') {
            return props.reviewContent.OriginalText ? formatXml(props.reviewContent.OriginalText) : '';
        }
        if (props.type === 'Metadata') {
            return props.reviewContent.MetaOriginalText ? formatXml(props.reviewContent.MetaOriginalText) : '';
        }
        return '';
    }

    const formatChangedValue = (changedValue: string) => {
        if(!changedValue || changedValue === '') {
            return ''
        }
        return props.type === 'Navigation'? JSON.parse(JSON.stringify(changedValue)):formatXml(changedValue)
    }

    /**
     * This code gets the current value of the editor, and then applies the changes that the user has made to it. 
     * It then sets the state of the editor to the new value.
     */
    useEffect(() => {
        if (props.reviewContent === null) {
            return;
        }

        // add try catch block
        try {
            let changedValue: string;
            const selectedRadioValue = props.selectedResult
            if(props.type === 'Navigation') {
                changedValue = selectedRadioValue === "Replace" &&
                props.replaceContent?.ReplacementNavChangedText ?
                props.replaceContent.ReplacementNavChangedText : props.reviewContent.NavChangedText
            }else if(props.type === 'Content') {
                changedValue = selectedRadioValue === "Replace" && 
                props.replaceContent && props.replaceContent.ReplacementChangedText ? 
                props.replaceContent.ReplacementChangedText : props.reviewContent.ChangedText
            }else if(props.type === 'Metadata') {
                changedValue = selectedRadioValue === "Replace" && 
                props.replaceContent && props.replaceContent.MetaReplacementChangedText ? 
                props.replaceContent.MetaReplacementChangedText : props.reviewContent.MetaChangedText
            }
            // format the xml
            let newEditorValue: IXmlDiffEditorState = {
                original: handleOriginalText(),
                value: formatChangedValue(changedValue)
            }

            setEditorValue(newEditorValue);

            props.onDiffEditorChange(props.type,changedValue);
        }
        catch(error)
        {
            console.error(error)
        }
    }, [props.reviewContent, props])

    function editorDidMount(editor) {
        // locate to the first diff line
        editor.onDidUpdateDiff(() => {
            const diffArr = editor.getLineChanges()
            if(diffArr.length > 0) {
                const diffStartLine = diffArr[0].modifiedStartLineNumber
                editor.revealLineInCenter(diffStartLine)
            }else {
                editor.revealLineInCenter(0)
            }
        });
    }

    /**
     * handle the change event of the editor
     * @param newValue 
     * @param event 
     */
    function onChange(newValue, event) {
        const minifyValue = props.type === 'Navigation'? newValue :
        formatXml.minify(newValue, {
            filter: (node) => node.type !== 'Comment',
            collapseContent: true
        });
        props.onDiffEditorChange(props.type,minifyValue);
    }

    return (
        <div style={{ width: "100%" }} aria-hidden="true" role="presentation">
            <div style={{ width: "100%", marginBottom: "20px" }} className="hide-print">
                <span style={{ marginLeft: "22%", marginRight: "20%", textAlign: "center" }} className="diff-input-header_labela">Original Text</span>
                <span style={{ marginLeft: "22%", marginRight: "20%", textAlign: "center" }} className="diff-input-header_labela">Changed Text</span>
            </div>
            <MonacoDiffEditor
                width="100%"
                height="300"
                language="xml"
                value={editorValue.value}
                original={editorValue.original}
                options={options}
                onChange={onChange}
                editorDidMount={editorDidMount}
            />
        </div>
    )
}

export default connect<StoreProps>(
    mapStateToProps
)(XmlDiffEditor);

